import { getNetworkId, isNetworkEVM, Networks } from "@vortexfi/shared";
import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { useAccount, useSwitchChain } from "wagmi";
import { getEnabledFrontendNetwork } from "../config/networkAvailability";
import { WALLETCONNECT_ASSETHUB_ID } from "../constants/constants";
import { LocalStorageKeys, useLocalStorage } from "../hooks/useLocalStorage";
import { useRampUrlParams } from "../hooks/useRampUrlParams";
import { useRampActor } from "./rampState";

interface NetworkContextType {
  walletConnectPolkadotSelectedNetworkId: string;
  selectedNetwork: Networks;
  setSelectedNetwork: (network: Networks, resetState?: boolean) => Promise<void>;
  networkSelectorDisabled: boolean;
  setNetworkSelectorDisabled: (disabled: boolean) => void;
}

const NetworkContext = createContext<NetworkContextType>({
  networkSelectorDisabled: false,
  selectedNetwork: getEnabledFrontendNetwork(undefined),
  setNetworkSelectorDisabled: () => null,
  setSelectedNetwork: async () => undefined,
  walletConnectPolkadotSelectedNetworkId: WALLETCONNECT_ASSETHUB_ID
});

interface NetworkProviderProps {
  children: ReactNode;
}

export const NetworkProvider = ({ children }: NetworkProviderProps) => {
  const { state: selectedNetworkLocalStorageState, set: setSelectedNetworkLocalStorage } = useLocalStorage<Networks>({
    defaultValue: Networks.Base,
    key: LocalStorageKeys.SELECTED_NETWORK
  });

  const { network } = useRampUrlParams();
  const rampActor = useRampActor();
  // We do this to ensure that the local storage value is always in lowercase. Previously the first letter was uppercase
  const selectedNetworkLocalStorage = selectedNetworkLocalStorageState.toLowerCase() as Networks;

  const [selectedNetwork, setSelectedNetworkState] = useState<Networks>(
    getEnabledFrontendNetwork(network || selectedNetworkLocalStorage)
  );
  const [networkSelectorDisabled, setNetworkSelectorDisabled] = useState(false);

  const { switchChainAsync } = useSwitchChain();
  const { chain: connectedEvmChain } = useAccount();

  const setSelectedNetwork = useCallback(
    async (network: Networks, resetState = false) => {
      if (resetState) {
        rampActor.send({ type: "RESET_RAMP" });
      }
      const enabledNetwork = getEnabledFrontendNetwork(network);
      setSelectedNetworkState(enabledNetwork);
      setSelectedNetworkLocalStorage(enabledNetwork);

      // Will only switch chain on the EVM connected wallet case.
      if (isNetworkEVM(enabledNetwork)) {
        // Only switch chain if the network is different from the current one
        // see https://github.com/wevm/wagmi/issues/3417
        if (!connectedEvmChain || connectedEvmChain.id !== getNetworkId(enabledNetwork)) {
          await switchChainAsync({ chainId: getNetworkId(enabledNetwork) });
        }
      }
    },
    [connectedEvmChain, switchChainAsync, setSelectedNetworkLocalStorage, rampActor]
  );

  return (
    <NetworkContext
      value={{
        networkSelectorDisabled,
        selectedNetwork,
        setNetworkSelectorDisabled,
        setSelectedNetwork,
        walletConnectPolkadotSelectedNetworkId: WALLETCONNECT_ASSETHUB_ID
      }}
    >
      {children}
    </NetworkContext>
  );
};

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error("useNetwork must be used within a NetworkProvider");
  }
  return context;
};
